"use client";

import { useState, useCallback } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Paper,
  Typography,
  LinearProgress,
  Alert,
  Chip,
  IconButton,
  Stack,
  Fade,
  Zoom,
  Slider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  CloudUpload,
  Delete,
  Close,
  CheckCircle,
  Image as ImageIcon,
  DragIndicator,
} from "@mui/icons-material";
import type { ImageConverterConfig } from "./registry";

interface ImageFile {
  file: File;
  preview: string;
  id: string;
}

interface ImageConverterProps {
  config: ImageConverterConfig;
}

export default function ImageConverter({ config }: ImageConverterProps) {
  const [images, setImages] = useState<ImageFile[]>([]);
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Initialize options from config
  const initializeOptions = () => {
    const opts: Record<string, string | number> = {};
    if (config.uiOptions) {
      config.uiOptions.forEach((option) => {
        opts[option.key] = option.defaultValue;
      });
    }
    return opts;
  };

  const [options, setOptions] =
    useState<Record<string, string | number>>(initializeOptions);

  const handleFileSelect = useCallback(
    (files: FileList | null) => {
      if (!files) return;

      const newImages: ImageFile[] = [];

      Array.from(files).forEach((file) => {
        // Check MIME type
        const isValidMime =
          config.acceptMime.length === 0 ||
          config.acceptMime.includes(file.type);
        // Check extension
        const fileName = file.name.toLowerCase();
        const isValidExtension =
          config.acceptExtensions.length === 0 ||
          config.acceptExtensions.some((ext) => fileName.endsWith(ext));

        if (isValidMime || isValidExtension) {
          const preview = URL.createObjectURL(file);
          newImages.push({
            file,
            preview,
            id: Math.random().toString(36).substr(2, 9),
          });
        } else {
          setError(
            `${file.name} is not a supported format. Please use ${config.fromFormats.join(", ")}.`
          );
        }
      });

      if (newImages.length > 0) {
        setImages((prev) => [...prev, ...newImages]);
        setError(null);
        setSuccess(false);
      }
    },
    [config]
  );

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFileSelect(e.dataTransfer.files);
      }
    },
    [handleFileSelect]
  );

  const removeImage = (id: string) => {
    setImages((prev) => {
      const updated = prev.filter((img) => img.id !== id);
      const removed = prev.find((img) => img.id === id);
      if (removed) {
        URL.revokeObjectURL(removed.preview);
      }
      return updated;
    });
    setError(null);
    setSuccess(false);
  };

  const clearAll = () => {
    images.forEach((img) => URL.revokeObjectURL(img.preview));
    setImages([]);
    setError(null);
    setSuccess(false);
    const fileInput = document.getElementById(
      "file-input"
    ) as HTMLInputElement | null;
    if (fileInput) fileInput.value = "";
  };

  const convertImage = async () => {
    if (images.length === 0) {
      setError(
        `Please select at least one ${config.fromFormats[0]} image to convert.`
      );
      return;
    }

    setConverting(true);
    setError(null);
    setSuccess(false);

    try {
      if (config.conversionEngine === "client") {
        await convertClientSide();
      } else {
        await convertServerSide();
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : `Failed to convert ${config.fromFormats[0]} to ${config.toFormats[0]}`
      );
    } finally {
      setConverting(false);
    }
  };

  const convertClientSide = async () => {
    // Client-side conversion logic
    try {
      for (const img of images) {
        const convertedBlob = await convertImageClient(
          img.file,
          config,
          options
        );
        const originalName = img.file.name.replace(/\.[^/.]+$/, "");
        const extension = config.toFormats[0].toLowerCase();
        downloadFile(convertedBlob, originalName, extension);
      }
      setSuccess(true);
      setTimeout(() => {
        clearAll();
      }, 2000);
    } catch (err) {
      throw err;
    }
  };

  const convertImageClient = async (
    file: File,
    config: ImageConverterConfig,
    opts: Record<string, string | number>
  ): Promise<Blob> => {
    // Special handling for SVG to PNG
    if (
      config.fromFormats.includes("SVG") &&
      config.toFormats.includes("PNG")
    ) {
      return convertSvgToPng(file, opts);
    }

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Could not get canvas context"));
          return;
        }

        ctx.drawImage(img, 0, 0);

        const quality = (opts.quality as number) ?? 0.95;
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error("Conversion failed"));
            }
          },
          getMimeType(config.toFormats[0]),
          quality
        );
      };
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = URL.createObjectURL(file);
    });
  };

  const convertSvgToPng = async (
    file: File,
    opts: Record<string, string | number>
  ): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const svgData = e.target?.result as string;
        const img = new Image();

        img.onload = () => {
          const canvas = document.createElement("canvas");
          // Use options or natural dimensions
          const width =
            (opts.width as number) ||
            (img.naturalWidth > 0 ? img.naturalWidth : 2000);
          const height =
            (opts.height as number) ||
            (img.naturalHeight > 0 ? img.naturalHeight : 2000);

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");
          if (!ctx) {
            reject(new Error("Could not get canvas context"));
            return;
          }

          // Set background based on option
          const background = (opts.background as string) || "transparent";
          if (background !== "transparent") {
            ctx.fillStyle = background;
            ctx.fillRect(0, 0, canvas.width, canvas.height);
          }
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error("SVG to PNG conversion failed"));
              }
            },
            "image/png",
            1.0
          );
        };

        img.onerror = () => {
          // If image load fails, try with explicit dimensions in SVG
          try {
            const parser = new DOMParser();
            const svgDoc = parser.parseFromString(svgData, "image/svg+xml");
            const svgElement = svgDoc.documentElement;

            // Set dimensions from options if not present
            const width = (opts.width as number) || 2000;
            const height = (opts.height as number) || 2000;
            if (
              !svgElement.getAttribute("width") &&
              !svgElement.getAttribute("viewBox")
            ) {
              svgElement.setAttribute("width", width.toString());
              svgElement.setAttribute("height", height.toString());
            }

            const modifiedSvg = new XMLSerializer().serializeToString(
              svgElement
            );
            const svgBlob = new Blob([modifiedSvg], { type: "image/svg+xml" });
            const url = URL.createObjectURL(svgBlob);
            img.src = url;
          } catch {
            reject(new Error("Failed to parse or convert SVG"));
          }
        };

        // Create object URL from SVG data
        const svgBlob = new Blob([svgData], { type: "image/svg+xml" });
        const url = URL.createObjectURL(svgBlob);
        img.src = url;
      };
      reader.onerror = () => reject(new Error("Failed to read SVG file"));
      reader.readAsText(file);
    });
  };

  const getMimeType = (format: string): string => {
    const mimeMap: Record<string, string> = {
      JPG: "image/jpeg",
      JPEG: "image/jpeg",
      PNG: "image/png",
      WebP: "image/webp",
    };
    return mimeMap[format] || "image/png";
  };

  const convertServerSide = async () => {
    if (!config.apiEndpoint) {
      throw new Error("API endpoint not configured for this converter");
    }

    // Check if API expects multiple files at once (like images-to-pdfa)
    const isMultiFile = config.apiEndpoint.includes("images-to-pdfa");

    if (isMultiFile) {
      // Send all files at once
      const formData = new FormData();
      images.forEach((img) => {
        formData.append("file", img.file);
      });
      // Add options
      Object.entries(options).forEach(([key, value]) => {
        formData.append(key, value.toString());
      });

      const apiUrl = process.env.NEXT_PUBLIC_API_URL || "";
      const endpoint = `${apiUrl}${config.apiEndpoint}`;

      try {
        const response = await axios.post(endpoint, formData, {
          timeout: 600000,
          responseType: "blob",
        });

        const blob = new Blob([response.data]);
        const originalName = "converted-images";
        const extension = config.toFormats[0].toLowerCase();
        downloadFile(blob, originalName, extension);
      } catch (error) {
        let errorMessage = "Conversion failed";
        if (axios.isAxiosError(error) && error.response) {
          try {
            const contentType = String(error.response.headers["content-type"] ?? "");
            if (contentType.includes("application/json")) {
              const errorText = await new Response(error.response.data).text();
              const errorData = JSON.parse(errorText);
              errorMessage = errorData.error || errorMessage;
            } else {
              errorMessage = `Server error: ${error.response.status}`;
            }
          } catch {
            errorMessage = `Server error: ${error.response.status}`;
          }
        }
        throw new Error(errorMessage);
      }
    } else {
      // Convert each image individually
      for (const img of images) {
        const formData = new FormData();
        formData.append("file", img.file);
        // Add options
        Object.entries(options).forEach(([key, value]) => {
          formData.append(key, value.toString());
        });

        const apiUrl = process.env.NEXT_PUBLIC_API_URL || "";
        const endpoint = `${apiUrl}${config.apiEndpoint}`;

        try {
          const response = await axios.post(endpoint, formData, {
            timeout: 600000,
            responseType: "blob",
          });

          const blob = new Blob([response.data]);
          const originalName = img.file.name.replace(/\.[^/.]+$/, "");

          // Check if response is a ZIP file (multiple pages) or single image
          const contentType = response.headers["content-type"];
          const isZip = contentType === "application/zip";
          const extension = isZip ? "zip" : config.toFormats[0].toLowerCase();

          downloadFile(blob, originalName, extension);
        } catch (error) {
          let errorMessage = "Conversion failed";
          if (axios.isAxiosError(error) && error.response) {
            try {
              const contentType = String(error.response.headers["content-type"] ?? "");
              if (contentType.includes("application/json")) {
                const errorText = await new Response(
                  error.response.data
                ).text();
                const errorData = JSON.parse(errorText);
                errorMessage = errorData.error || errorMessage;
              } else {
                errorMessage = `Server error: ${error.response.status}`;
              }
            } catch {
              errorMessage = `Server error: ${error.response.status}`;
            }
          }
          throw new Error(errorMessage);
        }
      }
    }

    setSuccess(true);
    setTimeout(() => {
      clearAll();
    }, 2000);
  };

  const downloadFile = (blob: Blob, baseName: string, format: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const extension = format.toLowerCase().replace("jpeg", "jpg");
    a.download = `${baseName}.${extension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const acceptString = [
    ...config.acceptMime,
    ...config.acceptExtensions.map((ext) => ext),
  ].join(",");

  return (
    <Box sx={{ maxWidth: 900, mx: "auto" }}>
      <Paper
        elevation={8}
        sx={{
          p: 4,
          borderRadius: 4,
          background: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(10px)",
        }}
      >
        {/* Upload Area */}
        <Box
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          sx={{
            border: "3px dashed",
            borderColor: dragActive ? "primary.main" : "grey.300",
            borderRadius: 3,
            p: 4,
            textAlign: "center",
            bgcolor: dragActive ? "primary.50" : "grey.50",
            cursor: "pointer",
            transition: "all 0.3s ease",
            position: "relative",
            "&:hover": {
              borderColor: "primary.main",
              bgcolor: "primary.50",
            },
          }}
          onClick={() => document.getElementById("file-input")?.click()}
        >
          <input
            id="file-input"
            type="file"
            multiple
            accept={acceptString}
            onChange={(e) => handleFileSelect(e.target.files)}
            style={{ display: "none" }}
          />

          <CloudUpload
            sx={{
              fontSize: 80,
              color: dragActive ? "primary.main" : "grey.400",
              mb: 2,
            }}
          />

          <Typography
            variant="h6"
            component="p"
            gutterBottom
            fontWeight={600}
            color="text.primary"
          >
            {dragActive
              ? "Drop your images here"
              : "Click to upload or drag and drop"}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Supported formats: {config.fromFormats.join(", ")}
          </Typography>

          <Button
            variant="contained"
            startIcon={<ImageIcon sx={{ color: "#ffffff" }} />}
            sx={{
              mt: 3,
              color: "#ffffff",
              backgroundColor: "#5558d3",
              "&:hover": {
                backgroundColor: "#4448c2",
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              document.getElementById("file-input")?.click();
            }}
          >
            Select Images
          </Button>
        </Box>

        {/* Selected Images Preview */}
        {images.length > 0 && (
          <Fade in={images.length > 0}>
            <Box sx={{ mt: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" component="p" fontWeight={600}>
                  Selected Images ({images.length})
                </Typography>
                <Button
                  size="small"
                  startIcon={<Delete />}
                  onClick={clearAll}
                  color="error"
                  variant="outlined"
                >
                  Clear All
                </Button>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, 1fr)",
                    sm: "repeat(3, 1fr)",
                    md: "repeat(4, 1fr)",
                  },
                  gap: 2,
                  maxHeight: 400,
                  overflowY: "auto",
                  p: 2,
                  bgcolor: "grey.50",
                  borderRadius: 2,
                }}
              >
                {images.map((img, index) => (
                  <Zoom
                    in
                    key={img.id}
                    style={{ transitionDelay: `${index * 50}ms` }}
                  >
                    <Paper
                      elevation={2}
                      sx={{
                        position: "relative",
                        aspectRatio: "1",
                        overflow: "hidden",
                        borderRadius: 2,
                        "&:hover .delete-button": {
                          opacity: 1,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundImage: `url(${img.preview})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                        }}
                      />

                      <Box
                        sx={{
                          position: "absolute",
                          top: 4,
                          left: 4,
                          bgcolor: "rgba(0,0,0,0.6)",
                          color: "white",
                          borderRadius: 1,
                          px: 1,
                          py: 0.5,
                          fontSize: "0.75rem",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <DragIndicator sx={{ fontSize: 14 }} />
                        {index + 1}
                      </Box>

                      <IconButton
                        className="delete-button"
                        size="small"
                        onClick={() => removeImage(img.id)}
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          bgcolor: "rgba(255,255,255,0.9)",
                          opacity: 0,
                          transition: "opacity 0.2s",
                          "&:hover": {
                            bgcolor: "error.main",
                            color: "white",
                          },
                        }}
                      >
                        <Close fontSize="small" />
                      </IconButton>

                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          right: 0,
                          bgcolor: "rgba(0,0,0,0.7)",
                          color: "white",
                          px: 1,
                          py: 0.5,
                          fontSize: "0.7rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {img.file.name}
                      </Box>
                    </Paper>
                  </Zoom>
                ))}
              </Box>
            </Box>
          </Fade>
        )}

        {/* Conversion Options */}
        {config.uiOptions && config.uiOptions.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Accordion defaultExpanded={true} sx={{ boxShadow: 2 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  bgcolor: "primary.50",
                  "&:hover": { bgcolor: "primary.100" },
                }}
              >
                <Typography variant="h6" fontWeight={600}>
                  Conversion Options
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {config.uiOptions.map((option) => (
                    <Box key={option.key}>
                      {option.type === "quality" && (
                        <Box>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <Typography variant="body1" fontWeight={600}>
                              {option.label}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {option.max && option.max <= 1
                                ? `${Math.round((options[option.key] as number) * 100)}%`
                                : `${options[option.key]}`}
                            </Typography>
                          </Box>
                          <Slider
                            value={options[option.key] as number}
                            onChange={(_, value) =>
                              setOptions({ ...options, [option.key]: value })
                            }
                            min={option.min}
                            max={option.max}
                            step={option.step}
                            marks
                            valueLabelDisplay="auto"
                            valueLabelFormat={(value) =>
                              option.max && option.max <= 1
                                ? `${Math.round(value * 100)}%`
                                : `${value}`
                            }
                            disabled={images.length === 0}
                          />
                          {option.description && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mt: 0.5, display: "block" }}
                            >
                              {option.description}
                            </Typography>
                          )}
                        </Box>
                      )}

                      {(option.type === "width" ||
                        option.type === "height") && (
                        <Box>
                          <TextField
                            fullWidth
                            type="number"
                            label={option.label}
                            value={options[option.key]}
                            onChange={(e) =>
                              setOptions({
                                ...options,
                                [option.key]:
                                  parseInt(e.target.value) ||
                                  option.defaultValue,
                              })
                            }
                            inputProps={{
                              min: option.min,
                              max: option.max,
                              step: option.step,
                            }}
                            helperText={option.description}
                            disabled={images.length === 0}
                          />
                        </Box>
                      )}

                      {option.type === "background" && (
                        <Box>
                          <FormControl fullWidth disabled={images.length === 0}>
                            <InputLabel>{option.label}</InputLabel>
                            <Select
                              value={options[option.key]}
                              label={option.label}
                              onChange={(e) =>
                                setOptions({
                                  ...options,
                                  [option.key]: e.target.value,
                                })
                              }
                            >
                              {option.options?.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {option.description && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mt: 0.5, display: "block" }}
                            >
                              {option.description}
                            </Typography>
                          )}
                        </Box>
                      )}

                      {option.type === "dpi" && (
                        <Box>
                          <FormControl fullWidth disabled={images.length === 0}>
                            <InputLabel>{option.label}</InputLabel>
                            <Select
                              value={options[option.key]}
                              label={option.label}
                              onChange={(e) =>
                                setOptions({
                                  ...options,
                                  [option.key]: e.target.value,
                                })
                              }
                            >
                              {option.options?.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {option.description && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mt: 0.5, display: "block" }}
                            >
                              {option.description}
                            </Typography>
                          )}
                        </Box>
                      )}

                      {option.type === "pages" && (
                        <Box>
                          <FormControl fullWidth disabled={images.length === 0}>
                            <InputLabel>{option.label}</InputLabel>
                            <Select
                              value={options[option.key]}
                              label={option.label}
                              onChange={(e) =>
                                setOptions({
                                  ...options,
                                  [option.key]: e.target.value,
                                })
                              }
                            >
                              {option.options?.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {option.description && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mt: 0.5, display: "block" }}
                            >
                              {option.description}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        )}

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mt: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mt: 3 }}
            icon={<CheckCircle />}
            onClose={() => setSuccess(false)}
          >
            Conversion successful! Your download should start automatically.
          </Alert>
        )}

        {/* Convert Button */}
        <Box sx={{ mt: 4 }}>
          {converting && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={<ImageIcon sx={{ color: "#ffffff" }} />}
            onClick={convertImage}
            disabled={converting || images.length === 0}
            style={{
              color: "#ffffff",
            }}
            sx={{
              py: 2,
              fontSize: "1.1rem",
              fontWeight: 600,
              background: "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
              boxShadow: "0 3px 15px rgba(102, 126, 234, 0.3)",
              "&:hover": {
                background: "linear-gradient(45deg, #5568d3 30%, #63408b 90%)",
                boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
              },
              "&.Mui-disabled": {
                background: "#e0e0e0",
                color: "rgba(0, 0, 0, 0.26)",
              },
            }}
          >
            {converting
              ? "Converting..."
              : `Convert ${images.length > 0 ? `${images.length} Image${images.length > 1 ? "s" : ""}` : ""} to ${config.toFormats[0]}`}
          </Button>

          {images.length > 0 && (
            <Stack
              direction="row"
              spacing={1}
              justifyContent="center"
              sx={{ mt: 2 }}
            >
              <Chip
                icon={<ImageIcon />}
                label={`${images.length} image${images.length > 1 ? "s" : ""} selected`}
                color="primary"
                variant="outlined"
              />
              <Chip
                label={`~${(images.reduce((acc, img) => acc + img.file.size, 0) / 1024 / 1024).toFixed(2)} MB`}
                variant="outlined"
              />
            </Stack>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
